import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  Grid,
  Typography,
  TextField,
  Autocomplete,
  MenuItem,
  Button,
  CircularProgress,
  Alert,
  Divider
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import expensesService from '../../services/expensesService';
import { settingsService } from '../../services/settingsService';

const CATEGORY_TITLE_MAP = [
  { category: 'Founder Support', title: 'Feeding, daily survival while working' },
  { category: 'Office', title: 'Office rent, electricity, furniture' },
  { category: 'Operations', title: 'General business operations' },
  { category: 'China Operations', title: 'Payments to workers in China' },
  { category: 'Warehouse', title: 'Warehouse fees in China' },
  { category: 'Packaging', title: 'Packaging materials' },
  { category: 'MArketing', title: 'Ads Campaign' },
  { category: 'Logistics', title: 'Shipping preparation' },
  { category: 'Communication', title: 'Airtime' },
  { category: 'Internet', title: 'Data' },
  { category: 'Transport', title: 'Transport to office/warehouse' },
  { category: 'Infrastructure', title: 'Servers, domains, hosting' }
];

const CATEGORY_OPTIONS = CATEGORY_TITLE_MAP.map((x) => x.category);
const TITLE_OPTIONS = CATEGORY_TITLE_MAP.map((x) => x.title);

const ExpenseCreate = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [cnyRate, setCnyRate] = useState(null);
  
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    category: '',
    expense_date: new Date().toISOString().slice(0, 10),
    original_amount: '',
    currency: 'NGN',
    vendor_name: '',
    payment_method: '',
    receipt: null
  });

  const [preview, setPreview] = useState({
    amount_in_ngn: 0,
    amount_in_cny: 0,
    exchange_rate: 0
  });

  const fetchRate = async () => {
    try {
      const settings = await settingsService.getPublicSettings('order_actual_rate');
      if (settings && settings.order_actual_rate) {
        setCnyRate(parseFloat(settings.order_actual_rate));
      }
    } catch (err) {
      console.error('Failed to fetch currency rates', err);
    }
  };

  const calculatePreview = useCallback(() => {
    const amount = parseFloat(formData.original_amount);
    if (!amount || isNaN(amount)) {
      setPreview({ amount_in_ngn: 0, amount_in_cny: 0, exchange_rate: cnyRate || 0 });
      return;
    }

    let ngn = 0;
    let cny = 0;
    const rate = cnyRate || 0;

    if (formData.currency === 'NGN') {
      ngn = amount;
      cny = rate > 0 ? amount / rate : 0;
    } else if (formData.currency === 'CNY') {
      cny = amount;
      ngn = amount * rate;
    }

    setPreview({
      amount_in_ngn: ngn,
      amount_in_cny: cny,
      exchange_rate: rate
    });
  }, [formData.original_amount, formData.currency, cnyRate]);

  useEffect(() => {
    fetchRate();
  }, []);

  useEffect(() => {
    calculatePreview();
  }, [calculatePreview]);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleCategoryInputChange = (_, value) => {
    setFormData((prev) => ({ ...prev, category: value || '' }));
  };

  const handleCategoryChange = (_, value) => {
    const selectedCategory = value || '';
    setFormData((prev) => {
      const match = CATEGORY_TITLE_MAP.find((x) => x.category === selectedCategory);
      return {
        ...prev,
        category: selectedCategory,
        // Auto-prefill title when category is selected from predefined options.
        title: match ? match.title : prev.title
      };
    });
  };

  const handleTitleInputChange = (_, value) => {
    setFormData((prev) => ({ ...prev, title: value || '' }));
  };

  const handleTitleChange = (_, value) => {
    setFormData((prev) => ({ ...prev, title: value || '' }));
  };

  const [receiptPreview, setReceiptPreview] = useState(null);

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      setFormData({ ...formData, receipt: file });
      if (file.type.startsWith('image/')) {
        const reader = new FileReader();
        reader.onloadend = () => {
          setReceiptPreview(reader.result);
        };
        reader.readAsDataURL(file);
      } else {
        setReceiptPreview(null);
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    const data = new FormData();
    Object.keys(formData).forEach(key => {
      if (formData[key] !== null) {
        data.append(key, formData[key]);
      }
    });

    try {
      await expensesService.createExpense(data);
      navigate('/admin/expenses');
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to create expense');
      setLoading(false);
    }
  };

  return (
    <Box sx={{ 
      minHeight: '100vh', 
      backgroundColor: 'background.default', 
      px: { xs: 0.75, sm: 1, md: 1.5 }, 
      py: { xs: 2, sm: 2.5, md: 3 }, 
      maxWidth: '95vw', 
      overflowX: 'hidden' 
    }}>
      <Typography variant="h4" gutterBottom>
        Create New Expense
      </Typography>

      <Card>
        <CardContent>
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          
          <form onSubmit={handleSubmit}>
            <Grid container spacing={3}>
              <Grid size={{ xs: 12 }}>
                <Typography variant="h6" gutterBottom>
                  Basic Information
                </Typography>
              </Grid>

              <Grid size={{ xs: 12, md: 4 }}>
                <Autocomplete
                  freeSolo
                  options={CATEGORY_OPTIONS}
                  value={formData.category}
                  inputValue={formData.category}
                  onChange={handleCategoryChange}
                  onInputChange={handleCategoryInputChange}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      fullWidth
                      label="Category"
                      name="category"
                      required
                    />
                  )}
                />
              </Grid>
                            <Grid size={{ xs: 12, md: 8 }}>
                <Autocomplete
                  freeSolo
                  options={TITLE_OPTIONS}
                  value={formData.title}
                  inputValue={formData.title}
                  onChange={handleTitleChange}
                  onInputChange={handleTitleInputChange}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      fullWidth
                      label="Title"
                      name="title"
                      required
                    />
                  )}
                />
              </Grid>

              <Grid size={{ xs: 12 }}>
                <TextField
                  fullWidth
                  multiline
                  rows={3}
                  label="Description"
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                />
              </Grid>

              <Grid size={{ xs: 12, md: 4 }}>
                <TextField
                  fullWidth
                  type="date"
                  label="Expense Date"
                  name="expense_date"
                  value={formData.expense_date}
                  onChange={handleChange}
                  InputLabelProps={{ shrink: true }}
                  required
                />
              </Grid>

              <Grid size={{ xs: 12 }}>
                <Divider sx={{ my: 2 }} />
                <Typography variant="h6" gutterBottom>
                  Amount & Currency
                </Typography>
              </Grid>

              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  fullWidth
                  type="number"
                  label="Original Amount"
                  name="original_amount"
                  value={formData.original_amount}
                  onChange={handleChange}
                  required
                />
              </Grid>

              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  select
                  fullWidth
                  label="Currency"
                  name="currency"
                  value={formData.currency}
                  onChange={handleChange}
                  required
                >
                  <MenuItem value="NGN">NGN (Nigerian Naira)</MenuItem>
                  <MenuItem value="CNY">CNY (Chinese Yuan)</MenuItem>
                </TextField>
              </Grid>

              {/* Live FX Preview */}
              <Grid size={{ xs: 12 }}>
                <Box sx={{ bgcolor: 'background.default', p: 2, borderRadius: 1 }}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Live FX Preview (Rate: {preview.exchange_rate})
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid size={{ xs: 6 }}>
                      <Typography variant="body2">Amount in NGN</Typography>
                      <Typography variant="h6">₦{preview.amount_in_ngn.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</Typography>
                    </Grid>
                    <Grid size={{ xs: 6 }}>
                      <Typography variant="body2">Amount in CNY</Typography>
                      <Typography variant="h6">¥{preview.amount_in_cny.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</Typography>
                    </Grid>
                  </Grid>
                </Box>
              </Grid>

              <Grid size={{ xs: 12 }}>
                <Divider sx={{ my: 2 }} />
                <Typography variant="h6" gutterBottom>
                  Vendor & Payment
                </Typography>
              </Grid>

              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  fullWidth
                  label="Vendor Name"
                  name="vendor_name"
                  value={formData.vendor_name}
                  onChange={handleChange}
                />
              </Grid>

              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  select
                  fullWidth
                  label="Payment Method"
                  name="payment_method"
                  value={formData.payment_method}
                  onChange={handleChange}
                >
                  <MenuItem value="">Select Method</MenuItem>
                  <MenuItem value="bank_transfer">Bank Transfer</MenuItem>
                  <MenuItem value="cash">Cash</MenuItem>
                  <MenuItem value="card">Card</MenuItem>
                  <MenuItem value="alipay">Alipay</MenuItem>
                  <MenuItem value="wechat">WeChat Pay</MenuItem>
                </TextField>
              </Grid>

              <Grid size={{ xs: 12 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Receipt
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Button variant="outlined" component="label">
                      {formData.receipt ? 'Change Receipt' : 'Upload Receipt'}
                      <input
                        type="file"
                        hidden
                        accept="image/*,application/pdf"
                        onChange={handleFileChange}
                      />
                    </Button>
                    {formData.receipt && (
                      <Typography variant="body2" color="text.secondary">
                        {formData.receipt.name}
                      </Typography>
                    )}
                  </Box>
                  {receiptPreview && (
                    <Box
                      component="img"
                      src={receiptPreview}
                      alt="Receipt Preview"
                      sx={{
                        width: '100%',
                        maxWidth: 300,
                        maxHeight: 300,
                        objectFit: 'contain',
                        border: 1,
                        borderColor: 'divider',
                        borderRadius: 1,
                        mt: 1
                      }}
                    />
                  )}
                </Box>
              </Grid>

              <Grid size={{ xs: 12 }} sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
                <Button onClick={() => navigate('/admin/expenses')}>
                  Cancel
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  disabled={loading}
                >
                  {loading ? <CircularProgress size={24} /> : 'Create Expense'}
                </Button>
              </Grid>
            </Grid>
          </form>
        </CardContent>
      </Card>
    </Box>
  );
};

export default ExpenseCreate;
